import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Shield, Database, Eye, Lock, Cookie, UserCheck, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-purple-900/20">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
              <Button 
                variant="ghost" 
                onClick={() => navigate(-1)}
                className="p-2 rounded-full"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  Privacy Policy
                </h1>
                <p className="text-muted-foreground">
                  Last updated: {new Date().toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="space-y-6">
              {/* Introduction */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-purple-600" />
                    Your Privacy Matters
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p>
                    At Positivio, we take your privacy seriously. This policy explains how we collect, 
                    use, and protect your personal information when you use our platform.
                  </p>
                  <p className="text-sm text-muted-foreground">
                    We believe in transparency and will never sell your data or use it for purposes 
                    beyond providing you with the best possible experience on our platform.
                  </p>
                </CardContent>
              </Card>

              {/* Information We Collect */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="w-5 h-5 text-blue-600" />
                    Information We Collect
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold flex items-center gap-2 mb-2">
                        <UserCheck className="w-4 h-4 text-green-500" />
                        Account Information
                      </h4>
                      <ul className="text-sm space-y-1 ml-6">
                        <li>• Email address and username</li>
                        <li>• Profile information (bio, avatar)</li>
                        <li>• Human verification data (securely encrypted)</li>
                        <li>• Friend connections and group memberships</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold flex items-center gap-2 mb-2">
                        <Eye className="w-4 h-4 text-purple-500" />
                        Usage Information
                      </h4>
                      <ul className="text-sm space-y-1 ml-6">
                        <li>• Posts, comments, and reactions</li>
                        <li>• Group interactions and messages</li>
                        <li>• Platform usage patterns (for improvement purposes)</li>
                        <li>• Device and browser information</li>
                      </ul>
                    </div>

                    <div>
                      <h4 className="font-semibold flex items-center gap-2 mb-2">
                        <Lock className="w-4 h-4 text-red-500" />
                        Verification Data
                      </h4>
                      <ul className="text-sm space-y-1 ml-6">
                        <li>• Photo ID verification (if chosen)</li>
                        <li>• Selfie video verification (if chosen)</li>
                        <li>• Phone number verification (if chosen)</li>
                        <li>• All verification data is encrypted and stored securely</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* How We Use Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-emerald-600" />
                    How We Use Your Information
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p>We use your information solely to:</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></span>
                        <span>Provide and maintain platform services</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></span>
                        <span>Verify human authenticity</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></span>
                        <span>Connect you with friends and groups</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></span>
                        <span>Improve platform features</span>
                      </li>
                    </ul>
                    <ul className="space-y-2 text-sm">
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></span>
                        <span>Send important platform updates</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></span>
                        <span>Maintain community safety</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></span>
                        <span>Comply with legal requirements</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></span>
                        <span>Provide customer support</span>
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Data Sharing */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Lock className="w-5 h-5 text-red-600" />
                    Data Sharing & Protection
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
                    <h4 className="font-semibold text-red-800 dark:text-red-400 mb-2">
                      We Never Sell Your Data
                    </h4>
                    <p className="text-sm text-red-700 dark:text-red-300">
                      We will never sell, rent, or trade your personal information to third parties for marketing purposes.
                    </p>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold mb-2">Limited Data Sharing</h4>
                    <p className="text-sm mb-2">We only share data in these specific circumstances:</p>
                    <ul className="text-sm space-y-1">
                      <li>• With your explicit consent</li>
                      <li>• To comply with legal obligations</li>
                      <li>• To protect user safety and platform security</li>
                      <li>• With trusted service providers under strict contracts</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>

              {/* Cookies & Tracking */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Cookie className="w-5 h-5 text-amber-600" />
                    Cookies & Tracking
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div>
                      <h4 className="font-semibold">Essential Cookies</h4>
                      <p className="text-sm text-muted-foreground">
                        Required for platform functionality (login, preferences, security)
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold">Analytics Cookies</h4>
                      <p className="text-sm text-muted-foreground">
                        Help us understand usage patterns to improve the platform (anonymized data only)
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold">No Advertising Cookies</h4>
                      <p className="text-sm text-muted-foreground">
                        We don't use advertising cookies or track you across other websites
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Your Rights */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserCheck className="w-5 h-5 text-purple-600" />
                    Your Privacy Rights
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p>You have the right to:</p>
                  <div className="grid md:grid-cols-2 gap-4 text-sm">
                    <ul className="space-y-2">
                      <li>• Access your personal data</li>
                      <li>• Correct inaccurate information</li>
                      <li>• Delete your account and data</li>
                      <li>• Export your data</li>
                    </ul>
                    <ul className="space-y-2">
                      <li>• Opt out of non-essential communications</li>
                      <li>• Control your privacy settings</li>
                      <li>• Object to certain data processing</li>
                      <li>• File complaints with authorities</li>
                    </ul>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                    <p className="text-sm text-purple-700 dark:text-purple-300">
                      To exercise any of these rights, contact us at{" "}
                      <span className="font-semibold">privacy@positivio.com</span>
                    </p>
                  </div>
                </CardContent>
              </Card>

              {/* Data Security */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-green-600" />
                    Data Security
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p>We protect your data using:</p>
                  <ul className="space-y-2 text-sm">
                    <li>• End-to-end encryption for sensitive data</li>
                    <li>• Secure cloud infrastructure</li>
                    <li>• Regular security audits and monitoring</li>
                    <li>• Strict access controls for our team</li>
                    <li>• Automated threat detection</li>
                    <li>• Regular data backups in secure locations</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Data Retention */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Database className="w-5 h-5 text-blue-600" />
                    Data Retention
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2 text-sm">
                    <li>• Account data: Retained while your account is active</li>
                    <li>• Verification data: Deleted when account is closed</li>
                    <li>• Posts/comments: Deleted when you delete them or close account</li>
                    <li>• Analytics data: Anonymized and retained for improvement purposes</li>
                    <li>• Legal compliance: Some data may be retained longer if required by law</li>
                  </ul>
                </CardContent>
              </Card>

              {/* Contact & Updates */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-purple-600" />
                    Contact & Policy Updates
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2">Privacy Questions?</h4>
                    <p className="text-sm">
                      Contact our privacy team at{" "}
                      <span className="font-semibold text-purple-600">privacy@positivio.com</span>
                    </p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Policy Changes</h4>
                    <p className="text-sm text-muted-foreground">
                      We'll notify you of significant privacy policy changes via email or platform notification. 
                      Minor updates will be posted here with an updated date.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PrivacyPolicy;